import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import BubbleButton from './BubbleButton';
import './BubbleNestedDropdown.scss';
import { useClickOutside } from './hooks/useClickOutside';
import type { BubbleButtonProps } from './BubbleButton';

export interface NestedDropdownItem<T> {
  key: string;
  element: string;
  submenuItems?: NestedDropdownItem<T>[];
}

interface BubbleNestedDropdownProps<T> extends BubbleButtonProps {
  items: NestedDropdownItem<T>[];
  onItemClick: (action: string, item: any) => void;
}

export default function BubbleNestedDropdown<T>({
  items,
  title,
  onItemClick,
  ...buttonProps
}: BubbleNestedDropdownProps<T>) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  function toggleOpen(key: string, e: React.MouseEvent) {
    e.stopPropagation();
    setOpenKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }

  function handleClick(key: string, item: any, e: React.MouseEvent) {
    e.stopPropagation();
    onItemClick(key, item);
    setOpen(false);
    setOpenKeys(new Set());
  }

  useEffect(() => {
    if (open && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const dropdownHeight = dropdownRef.current.scrollHeight;

      let top = buttonRect.bottom;
      let left = buttonRect.left;

      if (top + dropdownHeight > viewportHeight) {
        top = buttonRect.top - dropdownHeight;
      }

      setDropdownPosition({ top, left });
    }
  }, [open, items]);

  const clickOusideRef = useClickOutside(() => {
    setOpen(false);
    setOpenKeys(new Set());
  });

  const renderItems = (items: NestedDropdownItem<T>[], depth = 0) => (
    <ul
      className="bubble-dropdown-container fixed rounded border border-grey-lightest shadow-lg list-reset bg-white overflow-y-auto"
      style={depth === 0 ? dropdownPosition : {}}
      ref={depth === 0 ? dropdownRef : null}
    >
      {items.map(item => {
        const hasSubmenu = item.submenuItems && item.submenuItems.length > 0;
        const isOpen = openKeys.has(item.key);

        return (
          <li
            key={item.key}
            className="py-2 px-4 cursor-pointer list-item relative"
            onClick={e => {
              if (hasSubmenu) {
                toggleOpen(item.key, e);
              } else {
                handleClick(item.key, item, e);
              }
            }}
          >
            {item.element}
            {hasSubmenu && (
              <FontAwesomeIcon icon="chevron-right" className="ml-2 submenu-icon" fixedWidth />
            )}
            {hasSubmenu && isOpen && (
              <div className="sub-list-item">{renderItems(item.submenuItems!, depth + 1)}</div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={clickOusideRef}>
      {title && (
        <BubbleButton
          {...buttonProps}
          inverse
          onClick={() => setOpen(prev => !prev)}
          buttonRef={buttonRef}
        >
          {title}
        </BubbleButton>
      )}

      {!title && (
        <BubbleButton
          {...buttonProps}
          onClick={() => setOpen(prev => !prev)}
          buttonRef={buttonRef}
        />
      )}
      {open && renderItems(items)}
    </div>
  );
}
